import flatMap from 'lodash/flatMap';
import sumBy from 'lodash/sumBy';
import uniqBy from 'lodash/uniqBy';

import * as React from 'react';
import { PriceType } from '../../classes/price-type';
import { CartSeat, Seat, SeatPriceQuantity, SeatZone } from '../../classes/seat';
import QuantitySelect from '../../common/selects/quantity-select';
import { getCurrencyFormat } from '../../helpers/currency-format';
import { IAppSettingsModel } from '../../reducers/fetch-settings-reducer';

interface IPriceTypeQuantitiesProps {
    seats: Array<Seat>;
    selectedPriceTypes: Array<PriceType>;
    cartSeats: Array<CartSeat>;
    levelPricing: Array<SeatZone>;
    appSettingsModel: IAppSettingsModel;
    selectedPriceQuantities: Array<SeatPriceQuantity>;
    onSelectPricing: (priceQuantities: Array<SeatPriceQuantity>) => void;
    seatInCart?: boolean;
    isGAZone?: boolean;
}

interface IPriceTypeQuantitiesState {
    selectedPriceQuantities: Array<SeatPriceQuantity>;
    reloadPriceQuantity: boolean; //this is set once the intial price quantities have been loaded and this component state should take over
}

class PriceTypeQuantities extends React.Component<IPriceTypeQuantitiesProps, IPriceTypeQuantitiesState>{

    constructor(props) {
        super(props);
        this.state = { selectedPriceQuantities: this.getSelectedPrices(props), reloadPriceQuantity: true };
    }

    componentWillReceiveProps(nextProps: IPriceTypeQuantitiesProps) {
        if (this.state.reloadPriceQuantity) {
            this.setState({ selectedPriceQuantities: this.getSelectedPrices(nextProps), reloadPriceQuantity: true });
        }
    }

    getSelectedPrices(props: IPriceTypeQuantitiesProps): Array<SeatPriceQuantity> {
        const zonePrices = props.levelPricing.filter(pricing =>
            props.seats.map(seat => seat.tessituraSeat.zoneId)
                .some(zoneId => zoneId === pricing.zoneId.toString()));



        let selectedPrices = uniqBy(flatMap(zonePrices, zone => zone.prices.map(price => {
            price.zoneId = zone.zoneId;
            return price;
        })), 'priceTypeId');

        if (!props.isGAZone) {
            const selectedPriceTypeIds = props.selectedPriceTypes.map(pt => {
                return pt.priceTypeId;
            });

            selectedPrices = selectedPrices.filter(price => {
                return selectedPriceTypeIds.indexOf(+price.priceTypeId) >= 0;
            });
        } else {

        }

        //let filteredPrices = selectedPrices.filter(price =>
        //   props.appSettingsModel.priceTypeSettings.filter(ptSetting => ptSetting.priceTypeId === +price.priceTypeId)[0].maxQuantity <= 0);


        return selectedPrices.filter(price => props.appSettingsModel.priceTypeSettings.some(ptSetting => ptSetting.priceTypeId === +price.priceTypeId)).map(price => {
            let priceTypeSetting = props.appSettingsModel.priceTypeSettings.filter(ptSetting => ptSetting.priceTypeId === +price.priceTypeId)[0];
            let selectedPriceQuantity = props.selectedPriceQuantities.filter(selectedPQ => selectedPQ.seatPricing.priceTypeId === price.priceTypeId)[0];
            return {
                seatPricing: price,
                maxQuantity: Math.min(
                    (priceTypeSetting.clientMaxQuantity > 0 ? priceTypeSetting.clientMaxQuantity : 100),
                    props.seats.filter(seat => +seat.tessituraSeat.zoneId === price.zoneId && seat.tessituraSeat.isAvailable).length,
                    price.maxSeats),
                minQuantity: priceTypeSetting.clientMinQuantity,
                quantity: selectedPriceQuantity === undefined ? 0 : selectedPriceQuantity.quantity
            }
        })
    }

    _onSelectQuantity = (newQuantity: number, priceTypeId: number) => {
        const matchedPTQ = this.props.selectedPriceQuantities.filter(pt => pt.seatPricing.priceTypeId === priceTypeId);

        if (matchedPTQ.length > 0) {
            const newPriceQuantity: SeatPriceQuantity = { ...matchedPTQ[0], quantity: newQuantity };
            this.selectNewQuantity(newPriceQuantity);
        } else {
            // this means there are no tickets for this price type
            var stateMatchedPrice = this.state.selectedPriceQuantities.filter(pt => pt.seatPricing.priceTypeId === priceTypeId);
            if (stateMatchedPrice.length > 0) {
                const newPriceQuantity: SeatPriceQuantity = { ...stateMatchedPrice[0], quantity: newQuantity };
                this.selectNewQuantity(newPriceQuantity);
            }
        }
    }

    selectNewQuantity(newPriceQuantity: SeatPriceQuantity) {
        let newPriceQuanities = this.state.selectedPriceQuantities.map(priceQuantity => {
            if (newPriceQuantity.seatPricing.priceTypeId === priceQuantity.seatPricing.priceTypeId) {
                priceQuantity.quantity = newPriceQuantity.quantity;
            }

            return priceQuantity;
        });

        this.setState({
            ...this.state,
            reloadPriceQuantity: false,
            selectedPriceQuantities: newPriceQuanities
        });

        this.props.onSelectPricing(newPriceQuanities);
    }

    canAdd(priceQuantity: SeatPriceQuantity): boolean {
        const totalSeats = sumBy(this.props.selectedPriceQuantities, (pq) => pq.quantity);
        const maxSeats = this.props.seats.filter(seat => seat.tessituraSeat.isAvailable).length;
        return (totalSeats < maxSeats) && (priceQuantity.maxQuantity <= 0 || priceQuantity.quantity < priceQuantity.maxQuantity);
    }

    getMaxSeats(): number {
        return this.props.seats.filter(seat => seat.tessituraSeat.isAvailable).length;
    }

    canRemove(priceQuantity: SeatPriceQuantity): boolean {
        return (priceQuantity.minQuantity < priceQuantity.quantity && priceQuantity.quantity > 0);
    }

    getDisplayText(priceQuantity: SeatPriceQuantity): string {
        const priceText = getCurrencyFormat(this.props.appSettingsModel.currencyFormat, priceQuantity.seatPricing.price);
        const priceType = priceQuantity.seatPricing.priceTypeDescription;
        return `${priceText} - ${priceType}`;
    }

    render() {
        return (
            <div className="seat-info-prices" >
                <div className="seat-info-prices-inner">
                    <p className="seat-info-prices-header caption-paragraph">{this.props.appSettingsModel.gaZoneSelectTicketMessage}</p>
                    {this.state.selectedPriceQuantities.map(priceQuantity =>
                        <QuantitySelect
                            messageText=""
                            startingAtText=""
                            priceTypeMaxError={this.props.appSettingsModel.maxQuantityPriceTypeSelectedError.replace("{0}", priceQuantity.seatPricing.priceTypeDescription)}
                            priceTypeMaxReached={!this.canAdd(priceQuantity)}
                            priceTypeId={+priceQuantity.seatPricing.priceTypeId}
                            displayText={this.getDisplayText(priceQuantity)}
                            quantity={priceQuantity.quantity}
                            onSelectQuantity={this._onSelectQuantity}
                            canAdd={this.canAdd(priceQuantity)}
                            canRemove={this.canRemove(priceQuantity)}
                            key={priceQuantity.seatPricing.priceTypeId}
                            maxQuantity={this.getMaxSeats()}
                        />
                    )}
                </div>
            </div>
        );
    }
}

export default PriceTypeQuantities;